import {RiskReportTypes} from "../types";

export const getRiskFactorText = (factor: string, offendersCount: number): string => {
    if (factor.toLowerCase().includes('criminal'))
        return 'Criminal Records';
    if(offendersCount > 1)
        return 'Multiple Offenders'
    if(offendersCount > 1)
        return 'Sex Offenders Match'
    if(offendersCount > 1)
        return 'News Articles'
    if(offendersCount > 1)
        return 'Nearby Offenders'
    if(offendersCount > 1)
        return 'Public Records'
    if(offendersCount > 1)
        return 'Area Crime Data'
    if(offendersCount > 1)
        return 'Low Crime Area'
    if(offendersCount > 1)
        return 'High Crime Area'
    if(offendersCount > 1)
        return 'Safe Area'

    return factor
}

export const getRiskFactorChipStyle = (factor: string) => {
    if (factor.includes('Criminal')
        || factor.includes('Multiple')
        || factor.includes('Offender at Location')
        || factor.includes('Public Records')
        || factor.includes('High Crime')
        || factor.includes('Sex Offenders Match')
    ) {
        return { bgcolor: '#ffebee', color: '#c62828' };
    }
    if (factor.includes('Area Crime Data')) {
        return { bgcolor: '#206fa51a',  color: '#206FA5'}
    }
    // News is supplemental context, not a finding — neutral, not warning orange.
    if (factor.includes('News Articles')) {
        return { bgcolor: '#F4F6F8', color: '#637381' };
    }
    if (factor.includes('Low') || factor.includes('Nearby Offenders')) {
        return { bgcolor: '#ff95001a', color: '#FF9500' };
    }
    if (factor.includes('Safe')) {
        return { bgcolor: '#e8f5e8', color: '#2e7d32' };
    }
    return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
};

export const getSafetyScoreColor = (score: number): string => {
    return '#01579B';
};

export const getCrimeScoreRisk = (score: number, offendersCount: number) => {
    if(score === 0){
        return "N/A";
    }
    if (score > 60 && offendersCount > 1) {
        return "High Risk";
    }
    if (score >= 30 && score <= 59) { 
        return "Moderate Risk";
    }
    if (score <= 29) {
        return "Low Risk";
    }
}

export const getCrimeScoreBg = (riskText : string) => {
    if (riskText === "High Risk") {
        return "#FF0000";
    }
    if (riskText === "Moderate Risk") {
        return "#FAFF00";
    }
    if (riskText === "Low Risk") {
        return "#196000";
    }
}

// The server appends 'Possible Relevant News Found' to riskFactors when the
// news search kept any article. Articles are supplemental context rather than
// a finding (see news-articles.tsx), so the report keeps news-derived factors
// out of the Key Risk Factors buckets and counts; the Related News section
// carries them with its own caveat.
// Server strings (Node-API sexOffenderService): 'Possible Relevant News Found'
// and '<n> news articles with criminal references'. Matched on those two
// phrasings rather than any "news" so an unrelated factor is never dropped.
export const isSupplementalRiskFactor = (factor: string): boolean => /relevant news|news articles/i.test(factor);

// Helper to categorize risk factors
export const categorizeRiskFactors = (riskFactors: string[]) => {
    const categories = {
        criminalHistory: [] as string[],
        sexOffender: [] as string[],
        locationRisks: [] as string[],
        // Informational only: risk-factors.tsx renders the three buckets above
        // by name and must never render this one (see news-articles.tsx).
        supplemental: [] as string[],
    };

    riskFactors.forEach((factor) => {
        const lowerFactor = factor.toLowerCase();

        if (isSupplementalRiskFactor(factor)) {
            categories.supplemental.push(factor);
        } else if (lowerFactor.includes('crime rate') ||
            lowerFactor.includes('location') ||
            lowerFactor.includes('sex offender at exact location') ||
            lowerFactor.includes('area')) {
            categories.locationRisks.push(factor);
        } else if (lowerFactor.includes('sex offender') ||
            lowerFactor.includes('offender match') ||
            lowerFactor.includes('dob match')) {
            categories.sexOffender.push(factor);
        } else {
            // Everything else goes to criminal history
            categories.criminalHistory.push(factor);
        }
    });

    return categories;
};

export const getRiskFactorTexts = (data : RiskReportTypes.RiskResponse) => {
    let riskInfo = data?.riskAssessmentInfo;
    let areaRating = riskInfo?.findings?.addressInfo?.propertyInfo?.address?.areaSafetyRating
    let uniqueRiskFactors = riskInfo?.riskFactors ? Array.from(new Set(riskInfo.riskFactors)) : [];
    let criminalRiskCategories = categorizeRiskFactors(uniqueRiskFactors)?.criminalHistory
    let hasCriminalRecords = criminalRiskCategories && criminalRiskCategories.length > 0;
    let riskFactorMap = {
        'Criminal Records': hasCriminalRecords,
        'Multiple Offenders': data?.sexOffenderAtLocationCount > 1,
        'Offender at Location': data?.sexOffenderAtLocationCount === 1,
        'Sex Offenders Match': data?.nameMatchesInSexOffender?.possibleMatchFound && data?.nameMatchesInSexOffender?.birthDateMatch && data?.nameMatchesInSexOffender?.addressMatched,
        // Guarded: a single report with no `findings` (16 exist on DEV) threw
        // here and took the whole Risk Assessment history table down with it.
        'News Articles': (riskInfo?.findings?.newsArticles?.totalResults ?? 0) > 0,
        'Nearby Offenders': data?.sexOffenderNearby > 1,
        'Public Records': riskInfo?.totalRecordsFound > 0,
        'Area Crime Data': !!riskInfo?.findings?.addressInfo,
        'High Crime Area': areaRating !== undefined && areaRating <= 29,
        'Low Crime Area': areaRating !== undefined && areaRating >=29 && areaRating <= 60
    }
    let factors = Object.keys(riskFactorMap).filter(key => {
        return riskFactorMap[key as keyof typeof riskFactorMap];

    });
    if(factors.length)
        return factors
    return null
}

export const capitalizeFirstLetter = (str:string) =>{
    if(!str)
        return ''
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export const getPatientName = (data: RiskReportTypes.RiskResponse) => {
    const person = data?.riskAssessmentInfo?.searchedPerson;
    if (!person) return "N/A";
    const firstName = person.firstName ? capitalizeFirstLetter(person.firstName) : "";
    const lastName = person.lastName ? capitalizeFirstLetter(person.lastName) : "";
    return `${firstName} ${lastName}`.trim() || "N/A";
}

export const crimeScoreRiskDescription: Record<string, string> = {
    "High Risk": "High indicates this area has greater than 60 incidents per 1000 residents",
    "Moderate Risk": "Moderate indicates this area has between 30-59 incidents per 1000 residents",
    "Low Risk": "Low indicates this area has less than 30 incidents per 1000 residents",
    "N/A": "Crime data not available for the location",
}